'use client';
import { usePageError } from '@mentimeter/errors/usePageError';
import { DEFAULT_LOCALE, resolveTranslations } from '@mentimeter/i18n-core';
import { messages as creationMessages } from '@mentimeter/i18n-creation';
import { messages as ragnarMessages } from '@mentimeter/i18n-ragnar';
import { IntercomBubble } from '@mentimeter/intercom';
import { NextIntlClientProvider, useTranslations } from 'next-intl';

const messages = resolveTranslations(
  DEFAULT_LOCALE,
  creationMessages,
  ragnarMessages,
);

/**
 * ⚠️ ⚠️ ⚠️
 * Using `@mentimeter/ragnar-ui` in this component will throw
 * an error reading `renderRule`. Thus, we use inline styles.
 * ⚠️ ⚠️ ⚠️
 */
const FiveHundred = ({ error }: { error: Error }) => {
  const t = useTranslations('creation.page_errors');

  usePageError({
    error,
    message: 'Something went wrong and got caught in root-error',
    feature: 'root-error',
  });

  return (
    <body>
      <IntercomBubble />
      <div className="wrapper">
        <span className="text">{t('something_went_wrong_message')}</span>
        <button
          type="button"
          aria-label={t('reload_page_a11y')}
          onClick={() => window.location.reload()}
          className="button"
        >
          {t('click_to_reload_button')}
        </button>
      </div>
    </body>
  );
};

const ErrorPage = ({ error }: { error: Error }) => {
  return (
    <html lang={DEFAULT_LOCALE}>
      <head>
        <meta charSet="utf-8" />
        <link rel="preconnect" href="https://static.mentimeter.com" />
        <style>
          {`
            html, body {
              width: 100%;
              height: 100%;
              font-family: "MentiText";
            }

            @font-face {
              font-family: "MentiText";
              src:
                local("MentiText"),
                url("https://static.mentimeter.com/static/fonts/web/menti-text/1.4/MentiText-Regular.eot") format("embedded-opentype"),
                url("https://static.mentimeter.com/static/fonts/web/menti-text/1.4/MentiText-Regular.otf") format("opentype"),
                url("https://static.mentimeter.com/static/fonts/web/menti-text/1.4/MentiText-Regular.ttf") format("truetype"),
                url("https://static.mentimeter.com/static/fonts/web/menti-text/1.4/MentiText-Regular.woff") format("woff"),
                url("https://static.mentimeter.com/static/fonts/web/menti-text/1.4/MentiText-Regular.woff2") format("woff2");
            }

            .wrapper {
              display: flex;
              justify-content: center;
              align-items: center;
              flex-direction: column;
              width: 100%;
              height: 100%;
            }

            .button {
              background-color: rgb(237, 237, 240);
              border: 1px solid rgb(237, 237, 240);
              border-radius: 9999px;
              cursor: pointer;
              font-family: inherit;
              font-weight: 600;
              font-size: 1rem;
              margin-top: 4px;
              padding-bottom: 16px;
              padding-left: 32px;
              padding-right: 32px;
              padding-top: 16px;
              text-rendering: optimizelegibility;
              text-wrap-mode: nowrap;
              transition-delay: 0s;
              transition-duration: 0.3s;
              transition-property: all;
              transition-timing-function: ease;
            }

            .button:hover {
              border-color: rgb(228, 229, 233);
              background-color: rgb(228, 229, 233);
              color: rgb(22, 22, 23);
            }

            .text {
                color: rgba(0, 0, 0, 0.75);
                line-height: 1.4;
            }
          `}
        </style>
      </head>
      <NextIntlClientProvider locale={DEFAULT_LOCALE} messages={messages}>
        <FiveHundred error={error} />
      </NextIntlClientProvider>
    </html>
  );
};

export default ErrorPage;
